// src/components/RozwiazywanieSporow.js

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, ChevronDown, ChevronUp, Scale, Gavel, Users, MessageSquare, Landmark, FolderGit2, BookUser, ShieldCheck, Banknote, Building } from 'lucide-react';
import Trust from './Trust';
import ContactForm from './ContactForm';

// Add font import
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);

const RozwiazywanieSporow = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const disputeMethods = [
    { icon: <MessageSquare className="w-8 h-8" />, title: "Negocjacje", description: "Prowadzenie rozmów w celu osiągnięcia satysfakcjonującego porozumienia bez konieczności wszczynania postępowania sądowego." },
    { icon: <Users className="w-8 h-8" />, title: "Mediacje i arbitraż", description: "Reprezentacja w alternatywnych metodach rozwiązywania sporów, które często są szybsze i mniej kosztowne niż proces sądowy." },
    { icon: <Landmark className="w-8 h-8" />, title: "Postępowania sądowe", description: "Skuteczna reprezentacja klientów przed sądami powszechnymi, administracyjnymi i arbitrażowymi, na wszystkich etapach postępowania." },
    { icon: <FolderGit2 className="w-8 h-8" />, title: "Egzekucja należności", description: "Wsparcie w procesie odzyskiwania długów i egzekucji orzeczeń sądowych." },
  ];

  const disputeTypes = [
    "Spory gospodarcze",
    "Spory korporacyjne (między wspólnikami, organami spółki)",
    "Spory dotyczące umów handlowych",
    "Spory budowlane",
    "Spory z zakresu własności intelektualnej",
    "Spory podatkowe"
  ];

  const faqs = [
    {
      question: "Co to jest arbitraż i czy jest lepszy niż proces sądowy?",
      answer: "Arbitraż to pozasądowa metoda rozwiązywania sporów, w której strony powierzają rozstrzygnięcie sprawy bezstronnemu arbitrowi lub składowi arbitrażowemu. Często jest szybszy, mniej formalny i poufny niż proces sądowy, a wydany wyrok arbitrażowy ma taką samą moc prawną jak wyrok sądu."
    },
    {
      question: "Jakie są korzyści z mediacji w sporach gospodarczych?",
      answer: "Mediacja pozwala stronom na samodzielne wypracowanie rozwiązania pod okiem neutralnego mediatora. Główne korzyści to: krótszy czas trwania, niższe koszty, zachowanie dobrych relacji biznesowych i większa kontrola nad wynikiem sporu."
    },
    {
      question: "Czy specjalizujecie się w windykacji należności?",
      answer: "Tak, oferujemy kompleksowe wsparcie w procesie windykacji należności – od polubownego wezwania do zapłaty, przez postępowanie sądowe, aż po egzekucję komorniczą, dążąc do jak najszybszego odzyskania środków."
    }
  ];

  const handleContactScroll = () => {
    const contactSection = document.querySelector('#kontakt');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      {/* Hero Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 animate-float">
            <Scale className="w-32 h-32 text-violet-600" />
          </div>
          <div className="absolute bottom-20 left-20 animate-float animation-delay-2000">
            <Gavel className="w-24 h-24 text-violet-500" />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight uppercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Rozwiązywanie Sporów
            </h1>
             <p className="text-xl lg:text-2xl text-white mb-8 font-semibold max-w-4xl mx-auto">
              Skuteczna Obrona Twoich Interesów
            </p>
            <p className="text-lg text-gray-300 mb-12 max-w-3xl mx-auto">
              W obliczu konfliktu potrzebujesz partnera, który skutecznie obroni Twoje interesy. Szynkowski Legal oferuje kompleksowe wsparcie w rozwiązywaniu sporów – od negocjacji i mediacji, po reprezentację w postępowaniach sądowych i arbitrażowych.
            </p>
            <div className="flex justify-center">
              <button 
                onClick={handleContactScroll}
                className="group flex items-center gap-3 text-white text-xl lg:text-2xl font-medium hover:text-violet-500 transition-all duration-300 uppercase"
              >
                <span>Bezpłatna konsultacja</span>
                <ArrowRight className="w-6 h-6 text-violet-600 group-hover:text-violet-500 group-hover:translate-x-2 group-hover:scale-110 transition-all duration-300" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-6 md:mb-8 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Profesjonalna reprezentacja w sporach sądowych i pozasądowych
            </h2>
            <p className="text-lg text-gray-300">
              Spory biznesowe są nieodłącznym elementem działalności gospodarczej. Kluczem do sukcesu jest strategiczne podejście i skuteczne działanie. Nasz zespół prawników specjalizuje się w rozwiązywaniu sporów, dążąc do osiągnięcia najkorzystniejszego rozwiązania dla naszych klientów, zarówno na drodze polubownej, jak i sądowej.
            </p>
          </div>
        </div>
      </section>

      {/* Dispute Methods Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black text-white mb-4 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Nasze metody rozwiązywania sporów
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {disputeMethods.map((method, index) => (
              <div key={index} className="group backdrop-blur-md bg-white/5 rounded-xl p-8 border border-white/10 hover:border-violet-600/50 transition-all duration-300 hover:shadow-xl hover:shadow-violet-600/10">
                <div className="w-16 h-16 bg-gradient-to-br from-violet-600/20 to-violet-700/20 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <span className="text-violet-600">{method.icon}</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{method.title}</h3>
                <p className="text-gray-400">{method.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

    {/* Dispute Types Section */}
    <section className="py-16 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-violet-600/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black text-white mb-4 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Rodzaje sporów, w których specjalizujemy się
              </h2>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {disputeTypes.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <ShieldCheck className="w-6 h-6 text-violet-600 flex-shrink-0 mt-1" />
                  <span className="text-lg text-gray-300">{benefit}</span>
                </div>
              ))}
            </div>
            <div className="mt-12 text-center">
              <p className="text-xl text-gray-300 mb-8">
                Nie pozwól, aby spór sparaliżował Twój biznes. Skontaktuj się z nami po profesjonalną pomoc prawną!
              </p>
              <button 
                onClick={handleContactScroll}
                className="group flex items-center gap-3 text-white text-xl lg:text-2xl font-medium hover:text-violet-500 transition-all duration-300 uppercase mx-auto"
              >
                <span>Bezpłatna konsultacja</span>
                <ArrowRight className="w-6 h-6 text-violet-600 group-hover:text-violet-500 group-hover:translate-x-2 group-hover:scale-110 transition-all duration-300" />
              </button>
            </div>
          </div>
        </div>
      </section>


      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl lg:text-5xl font-black text-white mb-12 text-center uppercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
            FAQ: Rozwiązywanie Sporów
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="backdrop-blur-md bg-white/5 rounded-xl border border-white/10 overflow-hidden transition-all duration-300 hover:border-violet-600/30">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between group"
                >
                  <h3 className="text-lg font-semibold text-white pr-4">{faq.question}</h3>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-violet-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-violet-600 transition-colors flex-shrink-0" />
                  )}
                </button>
                <div className={`transition-all duration-300 ${openFaq === index ? 'max-h-96' : 'max-h-0'} overflow-hidden`}>
                  <div className="px-6 pb-4">
                    <p className="text-gray-400">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Trust />
      <ContactForm />

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(10deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
};

export default RozwiazywanieSporow;